import * as React from 'react';
import { Checkbox, IconSuccess16 } from '@cian/ui-kit';
import { useEventCallback } from '@cian/react-utils';
import classNames from 'classnames';
import * as styles from './SelectDropdown.css';
import { IOption, ISelectActions, TOptionValue } from '../../types';
import { ensureChildrenVisibility } from '../../utils';

export interface ISelectDropdownProps {
  options: IOption[];
  selectedItems: TOptionValue[];
  multiple?: boolean;
  focusedIndex: number;
  actions: ISelectActions;
  onKeyDown(e: React.KeyboardEvent): void;
}

export const SelectDropdown = React.forwardRef<HTMLDivElement, ISelectDropdownProps>(function SelectDropdown(
  { selectedItems, options, multiple, focusedIndex, actions, onKeyDown },
  ref,
) {
  const listRef = React.useRef<HTMLUListElement>(null);
  const focusedItemRef = React.useRef<HTMLLIElement>(null);

  React.useEffect(() => {
    const listContainer = listRef.current;
    const focusedItem = focusedItemRef.current;

    if (listContainer && focusedItem) {
      ensureChildrenVisibility(listContainer, focusedItem);
      focusedItemRef.current?.focus();
    }
  }, [focusedIndex]);

  const isItemActive = React.useCallback(
    (value: TOptionValue) => {
      return selectedItems.includes(value);
    },
    [selectedItems],
  );

  const handleOptionClick = useEventCallback((option: IOption) => () => {
    actions.selectOption(option);
  });

  const handleMouseMove = React.useCallback(
    (index: number, isTitle?: boolean) => () => {
      if (!isTitle && focusedIndex !== index) {
        actions.setFocusedIndex(index);
      }
    },
    [actions, focusedIndex],
  );

  return (
    <div ref={ref} className={styles['container']}>
      <ul ref={listRef} className={styles['list']} role="listbox" onKeyDown={onKeyDown}>
        {options.map(({ label, value, isTitle, disabled }, index) => {
          const active = isItemActive(value);
          const focused = index === focusedIndex;

          return (
            <li
              ref={focused ? focusedItemRef : undefined}
              className={classNames(styles['item'], {
                [styles['focused']]: focused,
                [styles['is-title']]: isTitle,
                [styles['item--disabled']]: disabled,
              })}
              key={`${value}-${index}`}
              role="option"
              tabIndex={0}
              onClick={handleOptionClick({ value, label, isTitle })}
              onMouseMove={handleMouseMove(index, isTitle)}
            >
              {multiple ? (
                <Checkbox
                  disabled={disabled}
                  checked={active}
                  label={label}
                  onClick={e => e.preventDefault()}
                  tabIndex={-1}
                />
              ) : (
                <>
                  {label}
                  {active && !isTitle && (
                    <div className={styles['icon']}>
                      <IconSuccess16 display="block" color="primary_100" />
                    </div>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
});
